const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
const videoExts = ["mp4", "mov", "ogv", "m4v", "webm", "flv", "f4v"];
const audioExts = ["mp3", "ogg", "wav", "flac", "m4a", "oga"];
const documentExts = [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
];
const archiveExts = ["zip", "rar", "7z"];
import { useSystemStore } from "~/stores/system";
import { useAuthStore } from "~/stores/auth";
import { ModuleEnum, PermissonEnum } from "~/constants/systemEnum";

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();
    const storageUrl = config.public.storageUrl;
    const getStorageUrl = (url: string) => {
        if (!url) {
            return "";
        }
        if (!url.startsWith("http"))
            return storageUrl + (url.startsWith("/") ? url : "/" + url);
        return url;
    };
    const getFileIdFromUrl = (url: string) => {
        if (!url) {
            return 0;
        }
        var urlWithoutDomain = url.replace(storageUrl, "");
        var nameWithoutExt = urlWithoutDomain.split(".").slice(0, -1).join(".");
        var id = nameWithoutExt.split("-").pop();
        return parseInt(id);
    };
    const thumb = (
        url: string,
        width: number = 800,
        height: number = 0,
        mode: string = "crop"
    ) => {
        if (url === null) {
            return "";
        }
        return (
            getStorageUrl(url) + "?w=" + width + "&h=" + height + "&mode=" + mode
        );
    };
    const thumbW = (url: string, width: number = 800) => {
        if (url === null) {
            return "";
        }
        return getStorageUrl(url) + "?w=" + width;
    };
    const thumbH = (url: string, height: number = 800) => {
        if (url === null) {
            return "";
        }
        return getStorageUrl(url) + "?h=" + height;
    };

    const getExtByType = (type: string) => {
        switch (type) {
            case "image":
                return imageExts;
            case "video":
                return videoExts;
            case "audio":
                return audioExts;
            case "document":
                return documentExts.concat(archiveExts);
            default:
                return [];
        }
    };

    const isImage = (ext: string) => {
        return imageExts.includes(ext);
    };
    const isVideo = (ext: string) => {
        return videoExts.includes(ext);
    };
    const isAudio = (ext: string) => {
        return audioExts.includes(ext);
    };
    const isDocument = (ext: string) => {
        return documentExts.includes(ext);
    };
    const isArchive = (ext: string) => {
        return archiveExts.includes(ext);
    };
    const niceBytes = (x: any) => {
        let l = 0,
            n = parseInt(x, 10) || 0;
        while (n >= 1000 && ++l) {
            n = n / 1000;
        }
        return n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l];
    };
    const date = (date: Date) => {
        if (date === null) {
            return "";
        }
        const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        return formattedDate;
    };
    const dateTime = (date: Date) => {
        if (date === null) {
            return "";
        }
        const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        const formattedTime = `${date.getHours()}:${date.getMinutes()}`;
        return `${formattedDate} ${formattedTime}`;
    };
    const getStoragePreviewUrl = (url: string) => {
        if (url === null) {
            return "";
        }
        return getStorageUrl(url) + "?preview=1";
    };
    const wh = () => {
        if (typeof window === "undefined") {
            return 0;
        }
        return window.innerHeight;
    };
    const ww = () => {
        if (typeof window === "undefined") {
            return 0;
        }
        return window.innerWidth;
    };
    const getExtWithDotByType = (type: string) => {
        return getExtByType(type).map((e) => "." + e);
    };
    const getExtByTypeToString = (type: string) => {
        return getExtByType(type).join(";");
    };
    const getThumbnail = (path: string, ext: string, width: number = 200) => {
        if (path === null) {
            return "";
        }
        if (isImage(ext)) return thumbW(path, width);
        else if (isVideo(ext))
            return thumbW(path, width); //path.replace('.' + ext, '') + '-thumb001.jpg'
        else if (isDocument(ext) || isArchive(ext) || isAudio(ext)) {
            return `/media/svg/files/${ext}.svg`;
        }
        return "/media/svg/files/file.svg";
    };

    const systemStore = useSystemStore();
    const isPermission = (
        module: number,
        ...permissions: number[]
    ) => {
        if (permissions.length === 0) {
            return false;
        }
        const modulePermissions = systemStore.systemData.userPermissions.find(
            (permission) => permission.module === module
        );
        if (
            !modulePermissions ||
            (!modulePermissions.isFull &&
                (!modulePermissions.permissions ||
                    !modulePermissions.permissions.length))
        ) {
            return false;
        }
        if (modulePermissions.isFull) {
            return true;
        }
        // Kiểm tra xem có ít nhất một permission nào được phép không
        return permissions.some((per) => {
            if (per === PermissonEnum.Allow) {
                return true;
            }
            const specificPermission = modulePermissions.permissions?.find(
                (p) => p.permission === per || p.permission === PermissonEnum.Full
            );
            return !!specificPermission;
        });
    };

    const getErrorMessage = (code: number) => {
        const o = systemStore.systemData.enums
            .find((k) => k.code === "ErrorCodeEnum")
            ?.items.find((k) => k.id === code);
        return o?.name;
    };
    const authStore = useAuthStore();
    const curUserId = authStore?.user?.id ?? 0;

    const userType = () => {
        const o = systemStore.systemData
        return o?.currentObject?.type;
    };

    const processingStatus = () => {
        const o = systemStore.systemData
        return o?.processingStatus;
    };

    return {
        provide: {
            niceBytes,
            date,
            dateTime,
            getStorageUrl,
            thumb,
            thumbW,
            thumbH,
            getStoragePreviewUrl,
            wh,
            ww,
            imageExts,
            videoExts,
            audioExts,
            documentExts,
            archiveExts,
            isImage,
            isVideo,
            isAudio,
            isDocument,
            isArchive,
            getExtByType,
            getExtWithDotByType,
            getExtByTypeToString,
            getThumbnail,
            isPermission,
            getErrorMessage,
            curUserId,
            getFileIdFromUrl,
            userType,
            processingStatus
        },
    };
});
